// src/pages/Counselling/Requests/Components/LeaderPicker.tsx
/**
 * Picks the leader a care request will be assigned to on approval.
 *
 * ⚠️ **The point of this component is the scope control.** `leaderService.list(typeId, zoneId, needId,
 * search)` supports server-side need and zone filtering that **no other screen in the app uses**, so a
 * request for Marriage in Zone 3 can open already showing the four leaders who cover both, rather than
 * all thirty-seven. That is what makes assigning "easy": the default is the right shortlist, not a
 * search box over everyone.
 *
 * The card list itself deliberately mirrors `CreateCaseWizard/Steps/Step3Assignment.tsx` so the two
 * places you pick a leader look and behave the same.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge, Card, Group, ScrollArea, SegmentedControl, Stack, Text, Avatar, Button,
} from '@mantine/core';
import { IconSearch, IconUserCheck } from '@tabler/icons-react';
import { TextInput } from '@/components';
import { useLeaderService, type LeaderModelView } from '@actschurch/ccms-sdk';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { resolveAvatarSrc, getInitialsFromName } from '@/utils/avatar';

export type LeaderScope = 'best' | 'need' | 'all';

export interface LeaderPickerProps {
  /** The request's need, used to narrow to leaders who cover it. */
  needID: number;
  /** The request's zone, when it has one. Without it the "best match" scope is not offered. */
  zoneID?: number | null;
  /**
   * The requester's gender, when they asked for a same-gender leader.
   *
   * ⚠️ Advisory only. `LeaderModelView.gender` is best-effort — it comes from the member cache and is
   * null when the leader's member record has no gender on file — so a match is **highlighted**, never
   * filtered. Filtering on a field that is sometimes null would silently hide valid leaders.
   */
  preferGender?: string | null;
  value: string | null;
  onChange: (userId: string | null) => void;
}

/** The several shapes a leader's name arrives in, in preference order. */
function leaderName(l: LeaderModelView): string {
  return [l.firstName, l.surname].filter(Boolean).join(' ').trim()
    || l.fullName?.trim()
    || l.displayName?.trim()
    || l.userId;
}

export default function LeaderPicker({
  needID, zoneID, preferGender, value, onChange,
}: LeaderPickerProps) {
  const leaderService = useLeaderService();

  const hasZone = zoneID !== null && zoneID !== undefined;
  const [scope, setScope] = useState<LeaderScope>(hasZone ? 'best' : 'need');
  const [leaders, setLeaders] = useState<LeaderModelView[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);

  const fetchLeaders = useCallback(async () => {
    setLoading(true);
    try {
      const term = debouncedSearch.trim() || undefined;
      const res = scope === 'best'
        ? await leaderService.list(undefined, zoneID ?? undefined, needID, term)
        : scope === 'need'
          ? await leaderService.list(undefined, undefined, needID, term)
          : await leaderService.list(undefined, undefined, undefined, term);
      setLeaders(res ?? []);
    } catch (e) {
      console.error('[LeaderPicker] failed to load leaders:', e);
      setLeaders([]);
    } finally {
      setLoading(false);
    }
  }, [leaderService, scope, zoneID, needID, debouncedSearch]);

  useEffect(() => { fetchLeaders(); }, [fetchLeaders]);

  /**
   * Re-applies the scope locally, and sorts least-loaded first.
   *
   * ⚠️ The local re-filter exists because a backend that ignores the `needId` / `zoneId` arguments
   * would otherwise make the scope control silently lie. An **absent** `needs` / `zones` array is
   * treated as "unknown, keep": list payloads in this API do drop collections (see
   * `06-known-issues.md` B27 for the same trap on `roles`), and dropping a real leader is worse than
   * showing one extra.
   */
  const visible = useMemo(() => {
    const matchesScope = (l: LeaderModelView) => {
      if (scope === 'all') return true;
      if (l.needs?.length && !l.needs.some((n) => n.needID === needID)) return false;
      if (scope === 'best' && hasZone && l.zones?.length
        && !l.zones.some((z) => z.zoneID === zoneID)) return false;
      return true;
    };

    const term = debouncedSearch.trim().toLowerCase();
    const matchesSearch = (l: LeaderModelView) => !term
      || [leaderName(l), l.email, l.phone, l.typeName]
        .some((f) => String(f ?? '').toLowerCase().includes(term));

    // With a same-gender preference, matching leaders float to the top; ties still break on load.
    // ⚠️ A leader with no gender on file sorts as a non-match rather than being dropped.
    const wanted = preferGender?.trim().toLowerCase();
    const genderRank = (l: LeaderModelView) =>
      wanted && l.gender?.trim().toLowerCase() === wanted ? 0 : 1;

    return leaders
      .filter((l) => matchesScope(l) && matchesSearch(l))
      .sort((a, b) => genderRank(a) - genderRank(b)
        || (a.activeCaseCount ?? 0) - (b.activeCaseCount ?? 0));
  }, [leaders, scope, needID, zoneID, hasZone, debouncedSearch, preferGender]);

  const selected = leaders.find((l) => l.userId === value) ?? null;

  return (
    <Stack gap="sm">
      <SegmentedControl
        value={scope}
        onChange={(v) => setScope(v as LeaderScope)}
        fullWidth
        size="xs"
        data={[
          { value: 'best', label: hasZone ? 'Covers need + zone' : 'Needs a zone', disabled: !hasZone },
          { value: 'need', label: 'Covers this need' },
          { value: 'all', label: 'All leaders' },
        ]}
      />

      <TextInput
        placeholder="Search leaders by name, email, phone or type…"
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        leftSection={<IconSearch size={16} />}
        radius="md"
        size="sm"
      />

      {selected && (
        <Card withBorder radius="md" padding="sm" style={{ borderColor: 'var(--mantine-primary-color-filled)' }}>
          <Group gap="sm" justify="space-between" wrap="nowrap">
            <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
              <IconUserCheck size={18} />
              <Text size="sm" fw={600} truncate>{leaderName(selected)}</Text>
            </Group>
            <Button size="compact-xs" variant="subtle" onClick={() => onChange(null)}>Clear</Button>
          </Group>
        </Card>
      )}

      {loading ? (
        <Text size="sm" c="dimmed" ta="center" py="md">Loading leaders…</Text>
      ) : visible.length === 0 ? (
        <Stack gap="xs" align="center" py="md">
          <Text size="sm" c="dimmed" ta="center">
            {scope === 'best'
              ? 'No leader covers both this need and this zone.'
              : scope === 'need'
                ? 'No leader covers this need.'
                : 'No leaders found.'}
          </Text>
          {/* Widen explicitly rather than silently: the admin should know the shortlist was empty. */}
          {scope === 'best' && (
            <Button size="compact-sm" variant="light" onClick={() => setScope('need')}>
              Widen to this need only
            </Button>
          )}
          {scope === 'need' && (
            <Button size="compact-sm" variant="light" onClick={() => setScope('all')}>
              Widen to all leaders
            </Button>
          )}
        </Stack>
      ) : (
        <ScrollArea.Autosize mah={260}>
          <Stack gap="xs">
            {visible.map((l) => {
              const name = leaderName(l);
              const isSelected = l.userId === value;
              return (
                <Card
                  key={l.userId}
                  withBorder
                  radius="md"
                  padding="sm"
                  role="button"
                  tabIndex={0}
                  style={{
                    cursor: 'pointer',
                    borderColor: isSelected ? 'var(--mantine-primary-color-filled)' : undefined,
                  }}
                  onClick={() => onChange(l.userId)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(l.userId); }
                  }}
                >
                  <Group gap="sm" wrap="nowrap" justify="space-between">
                    <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
                      <Avatar src={resolveAvatarSrc(l.avatar)} alt={name} radius="xl" size="sm">
                        {getInitialsFromName(name)}
                      </Avatar>
                      <div style={{ minWidth: 0 }}>
                        <Text size="sm" fw={600} truncate>{name}</Text>
                        <Text size="xs" c="dimmed" truncate>{l.email || l.phone || 'No contact'}</Text>
                      </div>
                    </Group>
                    <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                      {l.gender && (
                        <Badge
                          size="xs"
                          variant={preferGender && l.gender.trim().toLowerCase() === preferGender.trim().toLowerCase()
                            ? 'filled' : 'outline'}
                          color={preferGender && l.gender.trim().toLowerCase() === preferGender.trim().toLowerCase()
                            ? 'teal' : 'gray'}
                        >
                          {l.gender}
                        </Badge>
                      )}
                      {l.typeName && <Badge size="xs" variant="light">{l.typeName}</Badge>}
                      <Badge size="xs" variant="light" color={(l.activeCaseCount ?? 0) === 0 ? 'green' : 'gray'}>
                        {l.activeCaseCount ?? 0} open
                      </Badge>
                    </Group>
                  </Group>
                </Card>
              );
            })}
          </Stack>
        </ScrollArea.Autosize>
      )}

      {!loading && visible.length > 0 && (
        <Text size="xs" c="dimmed">
          {visible.length} leader{visible.length === 1 ? '' : 's'},{' '}
          {preferGender ? 'same gender first, then ' : ''}least busy first.
        </Text>
      )}
    </Stack>
  );
}
